// Thin wrappers around the /api/orgs organization-management endpoints
// (backend/modules/authz). Org visibility is enforced server-side: a
// platform_admin sees every org, an org_admin only their own.
import { fetchData } from '../fetchapi/fetchapi';

export async function listOrgs() {
  const res = await fetchData('orgs');
  return res.orgs || [];
}

export async function createOrg({ slug, name }) {
  return fetchData('orgs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug, name }),
  });
}

export async function listOrgMembers(orgSlug) {
  const res = await fetchData(`orgs/${encodeURIComponent(orgSlug)}/members`);
  return res.members || [];
}

// Adds (or re-roles) a member. If the user doesn't exist yet, pass a
// password to create the account in the same call (the invite flow).
export async function addOrgMember(orgSlug, { email, role, password, first_name, last_name }) {
  return fetchData(`orgs/${encodeURIComponent(orgSlug)}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role, password: password || undefined, first_name, last_name }),
  });
}

export async function removeOrgMember(orgSlug, email) {
  return fetchData(`orgs/${encodeURIComponent(orgSlug)}/members/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
}

// platform_admin only: move a project to another organization.
export async function moveProjectToOrg(projectSlug, orgSlug) {
  return fetchData(`projects/${encodeURIComponent(projectSlug)}/org`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ org_slug: orgSlug }),
  });
}

// Rename (org_admin+) / change status (platform_admin).
export async function updateOrg(orgSlug, { name, status }) {
  return fetchData(`orgs/${encodeURIComponent(orgSlug)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, status }),
  });
}

// platform_admin only; refused while the org still owns projects.
export async function deleteOrg(orgSlug) {
  return fetchData(`orgs/${encodeURIComponent(orgSlug)}`, { method: 'DELETE' });
}

// Edit a member's account (name / password reset) and/or the membership's
// subject attributes (the bag data-access policies evaluate).
export async function updateOrgMember(orgSlug, email, { first_name, last_name, password, attributes }) {
  return fetchData(`orgs/${encodeURIComponent(orgSlug)}/members/${encodeURIComponent(email)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ first_name, last_name, password: password || undefined, attributes }),
  });
}

// Configure the org's attribute provider — integration with a customer's
// existing entitlement/policies API. kind: 'static' | 'rest'.
export async function setAttributeProvider(orgSlug, provider) {
  return fetchData(`orgs/${encodeURIComponent(orgSlug)}/attribute-provider`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(provider),
  });
}

// platform_admin only: delete a user ACCOUNT globally (memberships cascade).
export async function deleteUserAccount(email) {
  return fetchData(`users/${encodeURIComponent(email)}`, { method: 'DELETE' });
}

// platform_admin only: set (base64 data-URI) or clear (null) the org's custom
// logo — shown in place of the Cortex logo for the org's members.
export async function setOrgLogo(orgSlug, logoData) {
  return fetchData(`orgs/${encodeURIComponent(orgSlug)}/logo`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ logo_data: logoData }),
  });
}
